import React from 'react';
import GridItem from '../../../../layout/GridComponents/GridItem';
import CustomTable from '../../../../tables/CustomTable';
import { CustomColumn } from '../../../../../types/components/tables/tableTypes';
import { mainColors } from '../../../../../styling/theme';
import LiquidityModelEvolutionChart from './liquidityModelEvolutionChart.component';
import type { LiquidityModelEvolutionPayload } from '../../../../../api/rs-hg/liquidity/hooks/queries';

interface LiquiditModelEvolutionTableProps {
  pageData: LiquidityModelEvolutionPayload;
  volumeOrDays: 'volume' | 'days';
}

export interface LiquiditModelEvolutionTableData {
  position_name: string;
  [key: string]: any; // Allow dynamic date keys
}

const createDetailColumns = (
  volumeOrDays: 'volume' | 'days',
  dates: string[],
): CustomColumn<LiquiditModelEvolutionTableData>[] => {
  const columns: CustomColumn<LiquiditModelEvolutionTableData>[] = [
    {
      title: 'Position',
      field: 'position_name',
      cellStyle: {
        textAlign: 'center',
        width: '20rem',
        minWidth: '15rem',
        backgroundColor: '#f5f5f5',
        fontWeight: 'bold',
        position: 'sticky',
        color: mainColors.mainBlue,
        left: 0,
        zIndex: 1,
      },
      headerStyle: {
        textAlign: 'center',
        width: '20rem',
        minWidth: '15rem',
        fontWeight: 'bold',
        position: 'sticky',
        left: 0,
        zIndex: 2,
      },
    },
  ];

  // Add dynamic date columns
  dates.forEach((date: string) => {
    columns.push({
      title: date,
      field: date,
      cellStyle: {
        textAlign: 'center',
        width: '7rem',
        minWidth: '7rem',
      },
      headerStyle: {
        textAlign: 'center',
        width: '7rem',
        minWidth: '7rem',
      },
      render: (rowData: any) => {
        const dateData = rowData[date];
        if (dateData && typeof dateData === 'object') {
          if (volumeOrDays === 'volume') {
            return typeof dateData.used_volume === 'number'
              ? dateData.used_volume.toLocaleString()
              : '';
          }
          return typeof dateData.used_days === 'number'
            ? dateData.used_days.toLocaleString()
            : '';
        }
        return '';
      },
    });
  });

  return columns;
};

const buildLiquidityTableData = (
  dates: string[],
  liquidityTableData: any[],
): LiquiditModelEvolutionTableData[] => {
  return liquidityTableData.map((item: any) => {
    const transformedItem: LiquiditModelEvolutionTableData = {
      position_name: item.position_name,
    };

    // Add each date as a field with the date data
    dates.forEach((date: string) => {
      if (item[date]) {
        transformedItem[date] = item[date];
      }
    });

    return transformedItem;
  });
};

const LiquiditModelEvolutionTable: React.FC<
  LiquiditModelEvolutionTableProps
> = ({ pageData, volumeOrDays }) => {
  const payload = pageData;
  const dates = payload?.dates || [];
  const liquidityTableData = payload?.liquidity_table || [];

  return (
    <GridItem xs={12} card>
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <CustomTable<LiquiditModelEvolutionTableData>
          columns={createDetailColumns(volumeOrDays, dates)}
          detailPanel={(rowData) => (
            <div style={{ padding: '1rem 2rem', background: '#fff' }}>
              <LiquidityModelEvolutionChart
                payload={payload}
                volumeOrDays={volumeOrDays}
                positionName={rowData.position_name}
              />
            </div>
          )}
          showToolbar
          data={buildLiquidityTableData(dates, liquidityTableData)}
          title={
            volumeOrDays === 'volume'
              ? 'Liquidity Model Evolution - Volume'
              : 'Liquidity Model Evolution - Days'
          }
          options={{
            paging: false,
            search: true,
            exportButton: true,
            maxBodyHeight: 'calc(100vh - 12.5rem)',
          }}
        />
      </div>
    </GridItem>
  );
};

export default LiquiditModelEvolutionTable;
